// ============================================================================
// nombresFr.js — les nombres à la française, SOURCE UNIQUE
// (refonte « Mosaïque vivante », lot L0).
// ----------------------------------------------------------------------------
// Charte Enio : virgule décimale, milliers séparés, espace insécable avant
// l'unité (« 34,0 M€ », « 258 h », « 15 % »). Les chiffres passent par
// Intl.NumberFormat("fr-FR") ; l'espace avant l'unité est posé ICI, toujours
// U+00A0, pour ne pas dépendre des données de langue du navigateur (selon la
// version, « % » est précédé d'U+202F ou d'U+00A0).
// Aucune dépendance : importable partout. Verrou : nombresFr.test.js.
// ============================================================================

export const INSECABLE = "\u00A0";
const TIRET = "\u2014"; // valeur absente ou non finie

const formats = new Map();
function formateur(min, max) {
  const cle = `${min}:${max}`;
  let f = formats.get(cle);
  if (!f) {
    f = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: min, maximumFractionDigits: max });
    formats.set(cle, f);
  }
  return f;
}

const estNombre = (n) => typeof n === "number" && Number.isFinite(n);

// 1234567 → « 1 234 567 ». { decimales: 1 } : jusqu'à une décimale ;
// { decimales: 1, fixe: true } : toujours une décimale (« 34,0 »).
export function nombreFr(n, { decimales = 0, fixe = false } = {}) {
  if (!estNombre(n)) return TIRET;
  return formateur(fixe ? decimales : 0, decimales).format(n);
}

// Valeur exprimée EN POINTS de pourcentage : 15 → « 15 % », 15.34 → « 15,3 % ».
export function pourcentFr(n, { decimales = 1 } = {}) {
  if (!estNombre(n)) return TIRET;
  return nombreFr(n, { decimales }) + INSECABLE + "%";
}

// Durée en heures : 258 → « 258 h », 1.5 → « 1,5 h ».
export function heuresFr(n, { decimales = 1 } = {}) {
  if (!estNombre(n)) return TIRET;
  return nombreFr(n, { decimales }) + INSECABLE + "h";
}

// Montant en euros, compact : « 1,02 Md€ », « 34,0 M€ », « 12 k€ », « 950 € ».
// Décimales FIXES au-delà du million (colonnes alignées), comme l'affichage
// historique du Panorama économique.
export function montantCompactFr(n) {
  if (!estNombre(n)) return TIRET;
  const signe = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a >= 1e9) return signe + nombreFr(a / 1e9, { decimales: 2, fixe: true }) + INSECABLE + "Md€";
  if (a >= 1e6) return signe + nombreFr(a / 1e6, { decimales: 1, fixe: true }) + INSECABLE + "M€";
  if (a >= 1e3) return signe + nombreFr(a / 1e3) + INSECABLE + "k€";
  return nombreFr(n) + INSECABLE + "€";
}

// Effectif compact (personnes, contrats) : « 1,2 M », « 12 k », « 950 ».
export function compteCompactFr(n) {
  if (!estNombre(n)) return TIRET;
  const a = Math.abs(n);
  if (a >= 1e6) return nombreFr(n / 1e6, { decimales: 1, fixe: true }) + INSECABLE + "M";
  if (a >= 1e3) return nombreFr(n / 1e3) + INSECABLE + "k";
  return nombreFr(n);
}
